import { useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Download } from "lucide-react";

const LAYERS = [
  { key: "boundary", label: "Boundary" },
  { key: "monuments", label: "Monuments" },
  { key: "parcels", label: "Parcels (GIS)" },
  { key: "contours", label: "Contours" },
  { key: "structures", label: "Structures" },
  { key: "improvements", label: "Improvements" },
  { key: "road", label: "Road & ROW" },
  { key: "utilities", label: "Utilities" },
  { key: "spots", label: "Spot Levels" },
  { key: "text", label: "Text" },
];

const STATUS_LABELS = {
  boundary: "BOUNDARY_CAD",
  monuments: "MONUMENTS_CAD",
  parcels: "PARCELS_GIS",
  contours: "CONTOURS_CAD",
  structures: "STRUCTURES_CAD",
  improvements: "IMPROVEMENTS_CAD",
  road: "ROW_ROAD_CAD",
  utilities: "UTILITIES_CAD",
  spots: "SPOT_ELEV_CAD",
  text: "TEXT_ANNOTATION",
  full: "FULL_SHEET",
  topo: "TOPO_CAD",
};

const PRESETS = {
  full: {
    label: "Full Sheet",
    layers: { boundary: true, monuments: true, parcels: true, contours: true, structures: true, improvements: true, road: true, utilities: true, spots: true, text: true },
  },
  topo: {
    label: "Topo View",
    layers: { boundary: true, monuments: false, parcels: false, contours: true, structures: false, improvements: false, road: false, utilities: false, spots: true, text: true },
  },
  boundary: {
    label: "Boundary Only",
    layers: { boundary: true, monuments: true, parcels: false, contours: false, structures: false, improvements: false, road: false, utilities: false, spots: false, text: true },
  },
};

const CONTOUR_PATHS = [
  "M-20 380C90 330 160 390 250 340S430 290 660 350",
  "M-20 350C90 300 160 360 250 310S430 260 660 320",
  "M-20 320C90 270 160 330 250 280S430 230 660 290",
  "M-20 290C90 240 160 300 250 250S430 200 660 260",
  "M-20 260C90 210 160 270 250 220S430 170 660 230",
  "M-20 230C90 180 160 240 250 190S430 140 660 200",
];

function Contours({ reduced }) {
  return (
    <g stroke="#20C4C7" strokeWidth="1.4" fill="none" opacity="0.7">
      {CONTOUR_PATHS.map((d, i) => (
        <motion.path
          key={i}
          d={d}
          initial={reduced ? false : { pathLength: 0, opacity: 0 }}
          whileInView={{ pathLength: 1, opacity: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 1.15, delay: 0.15 + i * 0.08, ease: "easeInOut" }}
        />
      ))}
    </g>
  );
}

function Parcels({ reduced }) {
  const parcels = [
    { d: "M20 40 L180 30 L170 190 L30 210 Z", id: "A-101", tx: 78, ty: 122 },
    { d: "M360 42 L620 32 L624 150 L400 164 Z", id: "A-102", tx: 492, ty: 100 },
    { d: "M18 232 L96 312 L58 384 L14 330 Z", id: "A-103", tx: 44, ty: 306 },
  ];
  return (
    <motion.g
      initial={reduced ? false : { opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ delay: 2.1, duration: 0.6 }}
    >
      {parcels.map((p) => (
        <g key={p.id}>
          <path d={p.d} fill="rgba(32,196,199,0.10)" stroke="#20C4C7" strokeWidth="1.2" strokeDasharray="5 4" opacity="0.85" />
          <text x={p.tx} y={p.ty} fontSize="9" fill="#0E7490" fontFamily="'JetBrains Mono', monospace">
            {p.id}
          </text>
        </g>
      ))}
    </motion.g>
  );
}

const BOUNDARY_VERTS = [
  [90, 120],
  [340, 80],
  [540, 160],
  [565, 315],
  [300, 372],
  [110, 300],
];

function Boundary({ reduced }) {
  return (
    <g>
      <motion.path
        d="M90 120 L340 80 L540 160 L565 315 L300 372 L110 300 Z"
        fill="rgba(32,196,199,0.06)"
        stroke="#20C4C7"
        strokeWidth="2.5"
        strokeLinejoin="round"
        initial={reduced ? false : { pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 1.6, delay: 0.1, ease: "easeInOut" }}
      />
      <motion.path
        d="M110 300 L300 372"
        stroke="#5EEAD4"
        strokeWidth="1.6"
        strokeDasharray="2 6"
        initial={reduced ? false : { opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 1.5, duration: 0.5 }}
      />
      {/* Front sidelines extended from the road-side corners to the ROW line */}
      <motion.path
        d="M565 315 L577 390 M300 372 L348 390"
        stroke="#5EEAD4"
        strokeWidth="1.4"
        strokeDasharray="2 6"
        fill="none"
        initial={reduced ? false : { opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 1.6, duration: 0.5 }}
      />
      {BOUNDARY_VERTS.map(([x, y], i) => (
        <motion.g
          key={i}
          initial={reduced ? false : { opacity: 0, scale: 0.4 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 1.3 + i * 0.06, duration: 0.3 }}
          style={{ transformBox: "fill-box", transformOrigin: "center" }}
        >
          <circle cx={x} cy={y} r="6" fill="#0B1D2F" stroke="#20C4C7" strokeWidth="2" />
          <circle cx={x} cy={y} r="1.8" fill="#20C4C7" />
        </motion.g>
      ))}
      <motion.text
        x="300"
        y="242"
        fill="#5EEAD4"
        fontSize="13"
        fontFamily="'JetBrains Mono', monospace"
        transform="rotate(-4 300 242)"
        textAnchor="middle"
        initial={reduced ? false : { opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 1.7, duration: 0.5 }}
      >
        LOT 12
      </motion.text>
    </g>
  );
}

function Monuments({ reduced }) {
  const mono = "'JetBrains Mono', monospace";
  // Monument symbols just outside each corner: iron rods = diamond pin, iron pipes = open circle
  const pins = [
    { x: 81, y: 116, type: "rod" },
    { x: 341, y: 70, type: "rod" },
    { x: 550, y: 157, type: "pipe" },
    { x: 574, y: 319, type: "rod" },
    { x: 298, y: 382, type: "rod" },
    { x: 101, y: 303, type: "pipe" },
  ];
  // Northing/Easting pairs + monument callout beside each corner (V4 annotated inside the lot)
  const coords = [
    { x: 44, y: 82, anchor: "middle", n: "N 512486.42", e: "E 2087330.15", m: 'FND 1/2" IRON ROD' },
    { x: 344, y: 34, anchor: "middle", n: "N 512540.18", e: "E 2087580.44", m: 'SET 5/8" IRON ROD' },
    { x: 576, y: 146, anchor: "start", n: "N 512438.77", e: "E 2087805.62", m: 'FND 1" IRON PIPE' },
    { x: 634, y: 323, anchor: "end", n: "N 512286.05", e: "E 2087830.90", m: 'SET 1/2" IRON ROD' },
    { x: 283, y: 345, anchor: "middle", n: "N 512192.33", e: "E 2087546.28", m: 'FND 5/8" IRON ROD' },
    { x: 78, y: 307, anchor: "end", n: "N 512272.60", e: "E 2087198.37", m: 'FND 1" IRON PIPE' },
  ];
  return (
    <motion.g
      initial={reduced ? false : { opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ delay: 1.45, duration: 0.5 }}
    >
      {pins.map((p, i) =>
        p.type === "rod" ? (
          <g key={i} transform={`rotate(45 ${p.x} ${p.y})`}>
            <rect x={p.x - 3.2} y={p.y - 3.2} width="6.4" height="6.4" fill="#102A43" stroke="#20C4C7" strokeWidth="1.2" />
            <circle cx={p.x} cy={p.y} r="1" fill="#20C4C7" />
          </g>
        ) : (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r="4.2" fill="#FFFFFF" stroke="#102A43" strokeWidth="1.4" />
            <circle cx={p.x} cy={p.y} r="1.2" fill="#102A43" />
          </g>
        )
      )}
      {coords.map((c, i) => (
        <text key={i} x={c.x} y={c.y} fontSize="6.5" fill="#62758A" fontFamily={mono} textAnchor={c.anchor}>
          <tspan x={c.x} dy="0">{c.n}</tspan>
          <tspan x={c.x} dy="9">{c.e}</tspan>
          <tspan x={c.x} dy="9" fill="#176B87">{c.m}</tspan>
        </text>
      ))}
    </motion.g>
  );
}

function Structures({ reduced }) {
  return (
    <motion.g
      stroke="#64748B"
      strokeWidth="1.6"
      fill="#FFFFFF"
      fillOpacity="0.9"
      initial={reduced ? false : { opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ delay: 1.55, duration: 0.6 }}
    >
      <rect x="195" y="150" width="115" height="82" />
      <rect x="330" y="140" width="72" height="52" />
      <rect x="150" y="275" width="42" height="30" fill="#F1F5F9" />
      <rect x="450" y="150" width="52" height="36" fill="#F1F5F9" />
      <line x1="450" y1="150" x2="476" y2="168" stroke="#94A3B8" strokeWidth="0.9" />
      <line x1="502" y1="150" x2="476" y2="168" stroke="#94A3B8" strokeWidth="0.9" />
    </motion.g>
  );
}

function Improvements({ reduced }) {
  return (
    <motion.g
      stroke="#64748B"
      strokeWidth="1.6"
      fill="#FFFFFF"
      fillOpacity="0.9"
      initial={reduced ? false : { opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ delay: 1.65, duration: 0.6 }}
    >
      <rect x="205" y="255" width="72" height="50" fill="#F1F5F9" />
      <line x1="205" y1="255" x2="255" y2="305" stroke="#CBD5E1" strokeWidth="0.9" />
      <line x1="228" y1="255" x2="277" y2="304" stroke="#CBD5E1" strokeWidth="0.9" />
      <line x1="251" y1="255" x2="277" y2="281" stroke="#CBD5E1" strokeWidth="0.9" />
      <rect x="330" y="252" width="84" height="46" fill="#F1F5F9" />
      <line x1="344" y1="264" x2="400" y2="264" stroke="#94A3B8" strokeWidth="0.9" />
      <line x1="344" y1="276" x2="400" y2="276" stroke="#94A3B8" strokeWidth="0.9" />
      <line x1="344" y1="288" x2="400" y2="288" stroke="#94A3B8" strokeWidth="0.9" />
      <path d="M442 404 C 438 372, 436 340, 436 310" fill="none" stroke="#475569" strokeWidth="1.6" />
      <path d="M466 404 C 462 372, 460 340, 460 310" fill="none" stroke="#475569" strokeWidth="1.6" />
      <path d="M95 108 C 200 96, 300 112, 400 100 S 540 108, 560 118" fill="none" stroke="#0E7490" strokeWidth="1.4" strokeDasharray="6 4" />
      <rect x="0" y="392" width="640" height="8" fill="#E2E8F0" stroke="none" />
      {Array.from({ length: 16 }).map((_, i) => (
        <line key={i} x1={i * 40 + 20} y1="392" x2={i * 40 + 20} y2="400" stroke="#CBD5E1" strokeWidth="1" />
      ))}
    </motion.g>
  );
}

function RoadRow({ reduced }) {
  const lineProps = (delay) => ({
    initial: reduced ? false : { pathLength: 0 },
    whileInView: { pathLength: 1 },
    viewport: { once: true, amount: 0.2 },
    transition: { duration: 0.9, delay, ease: "easeInOut" },
  });
  return (
    <g>
      <motion.line x1="0" y1="390" x2="640" y2="390" stroke="#64748B" strokeWidth="1.4" {...lineProps(0.9)} />
      <motion.line x1="0" y1="446" x2="640" y2="446" stroke="#64748B" strokeWidth="1.4" {...lineProps(1.0)} />
      <motion.line x1="0" y1="404" x2="640" y2="404" stroke="#475569" strokeWidth="1.6" {...lineProps(1.1)} />
      <motion.line x1="0" y1="436" x2="640" y2="436" stroke="#475569" strokeWidth="1.6" {...lineProps(1.1)} />
      <motion.line x1="0" y1="420" x2="640" y2="420" stroke="#94A3B8" strokeWidth="1.2" strokeDasharray="14 10" {...lineProps(1.35)} />
    </g>
  );
}

function PlanText({ reduced }) {
  const mono = "'JetBrains Mono', monospace";
  return (
    <motion.g
      initial={reduced ? false : { opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ delay: 2.0, duration: 0.6 }}
    >
      {/* Adjoining property lines (offset outside the subject boundary, extended past the corners) */}
      <g stroke="#94A3B8" strokeWidth="1.1" strokeDasharray="7 5" fill="none">
        <path d="M68 101 L354 55" />
        <path d="M333 54 L562 148" />
        <path d="M559 141 L590 328" />
        <path d="M90 316 L66 108" />
      </g>
      <text x="210" y="70" fontSize="7" fill="#94A3B8" fontFamily={mono} textAnchor="middle" transform="rotate(-9.1 210 70)">N/F T. KOWALSKI — LOT 10</text>
      <text x="451" y="92" fontSize="7" fill="#94A3B8" fontFamily={mono} textAnchor="middle" transform="rotate(21.8 451 92)">N/F M. CHEN — LOT 11</text>
      <text x="592" y="231" fontSize="7" fill="#94A3B8" fontFamily={mono} textAnchor="middle" transform="rotate(80.8 592 231)">N/F A. RIVERA — LOT 13</text>
      <text x="58" y="240" fontSize="7" fill="#94A3B8" fontFamily={mono} textAnchor="middle" transform="rotate(83.7 58 240)">N/F S. PATEL — LOT 9</text>

      {/* Bearings & distances — one per boundary side, centred on and parallel to each line */}
      <text x="215" y="86" fontSize="9.5" fill="#176B87" fontFamily={mono} textAnchor="middle" transform="rotate(-9.1 215 86)">{'S 79°52\'04" E  210.35\''}</text>
      <text x="445" y="108" fontSize="9.5" fill="#176B87" fontFamily={mono} textAnchor="middle" transform="rotate(21.8 445 108)">{'S 68°12\'40" E  215.40\''}</text>
      <text x="560" y="230" fontSize="9.5" fill="#176B87" fontFamily={mono} textAnchor="middle" transform="rotate(-80 560 230)">{'N 12°45\'30" E  98.76\''}</text>
      <text x="435" y="356" fontSize="9.5" fill="#176B87" fontFamily={mono} textAnchor="middle" transform="rotate(-12.1 435 356)">{'S 77°49\'55" W  271.05\''}</text>
      <text x="201" y="349" fontSize="9.5" fill="#176B87" fontFamily={mono} textAnchor="middle" transform="rotate(20.8 201 349)">{'N 43°12\'18" W  132.45\''}</text>
      <text x="87" y="241" fontSize="9.5" fill="#176B87" fontFamily={mono} textAnchor="middle" transform="rotate(83.7 87 241)">{'N 6°20\'10" W  181.12\''}</text>

      {/* Subject lot — legal description and area */}
      <text x="450" y="208" fontSize="8.5" fill="#176B87" fontFamily={mono} textAnchor="middle" fontWeight="bold">LOT 12 · BLOCK B</text>
      <text x="450" y="219" fontSize="7.5" fill="#176B87" fontFamily={mono} textAnchor="middle">MEADOWBROOK ESTATES SUBDIVISION</text>
      <text x="450" y="230" fontSize="7.5" fill="#176B87" fontFamily={mono} textAnchor="middle">{"AREA: 43,560 SQ. FT. = 1.000 AC."}</text>

      <text x="252" y="196" fontSize="9" fill="#475569" fontFamily={mono} textAnchor="middle">HOUSE</text>
      <text x="366" y="170" fontSize="9" fill="#475569" fontFamily={mono} textAnchor="middle">GARAGE</text>
      <text x="476" y="172" fontSize="9" fill="#475569" fontFamily={mono} textAnchor="middle">BARN</text>
      <text x="241" y="284" fontSize="9" fill="#475569" fontFamily={mono} textAnchor="middle">PATIO</text>
      <text x="372" y="280" fontSize="9" fill="#475569" fontFamily={mono} textAnchor="middle">DECK</text>
      <text x="171" y="294" fontSize="9" fill="#475569" fontFamily={mono} textAnchor="middle">SHED</text>
      <text x="470" y="332" fontSize="8.5" fill="#475569" fontFamily={mono}>DRIVEWAY</text>
      <text x="522" y="400" fontSize="8.5" fill="#64748B" fontFamily={mono}>SIDEWALK</text>
      <text x="120" y="124" fontSize="8.5" fill="#0E7490" fontFamily={mono}>DRAINAGE SWALE</text>
      <text x="320" y="424" fontSize="10" fill="#475569" fontFamily={mono} textAnchor="middle" letterSpacing="2">COUNTY ROAD 12</text>
      <text x="30" y="386" fontSize="8.5" fill="#64748B" fontFamily={mono}>R/O-W 60.0'</text>
    </motion.g>
  );
}

function Utilities({ reduced }) {
  return (
    <motion.g
      fill="none"
      strokeWidth="1.8"
      strokeDasharray="7 5"
      initial={reduced ? false : { opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ delay: 1.9, duration: 0.6 }}
    >
      <path d="M60 90 C 200 150, 380 130, 620 210" stroke="#F59E0B" opacity="0.8" />
      <path d="M40 340 C 220 300, 420 380, 640 300" stroke="#38BDF8" opacity="0.8" />
    </motion.g>
  );
}

function Spots({ reduced }) {
  const spots = [
    [150, 210, "412.8"],
    [330, 130, "415.2"],
    [480, 260, "409.6"],
    [250, 330, "411.3"],
    [530, 180, "414.0"],
  ];
  return (
    <motion.g
      initial={reduced ? false : { opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ delay: 1.8, duration: 0.6 }}
    >
      {spots.map(([x, y, v], i) => (
        <g key={i}>
          <path d={`M${x} ${y - 5} l5 9 h-10 Z`} fill="#102A43" />
          <text x={x + 10} y={y + 6} fontSize="10.5" fill="#62758A" fontFamily="'JetBrains Mono', monospace">
            EL {v}
          </text>
        </g>
      ))}
    </motion.g>
  );
}

const DEFAULT_LAYERS = {
  boundary: true,
  monuments: true,
  parcels: false,
  contours: true,
  structures: true,
  improvements: true,
  road: true,
  utilities: false,
  spots: true,
  text: true,
};

export default function CadViewer({ focus = null, onLayerSelect = null, initialLayers = null, compact = false }) {
  const [layers, setLayers] = useState(initialLayers ? { ...DEFAULT_LAYERS, ...initialLayers } : { ...DEFAULT_LAYERS });
  const [status, setStatus] = useState(focus || "boundary");
  const reduced = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const wrap = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-6, 6]), { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = wrap.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };
  const toggle = (key) => {
    setLayers((l) => ({ ...l, [key]: !l[key] }));
    setStatus(key);
    if (onLayerSelect) onLayerSelect(key);
  };
  const applyPreset = (key) => {
    setLayers({ ...PRESETS[key].layers });
    setStatus(key);
  };

  const layerWrap = (key, node) =>
    layers[key] ? (
      <g key={key} opacity={focus && key !== focus ? 0.32 : 1} style={{ transition: "opacity 0.3s ease" }}>
        {node}
      </g>
    ) : null;

  return (
    <motion.div
      ref={wrap}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      initial={{ opacity: 0, y: 44, rotateX: 10 }}
      animate={{ opacity: 1, y: 0, rotateX: 0 }}
      transition={{ duration: 1, delay: 0.35, ease: [0.215, 0.61, 0.355, 1] }}
      style={{ rotateX, rotateY, transformPerspective: 1200 }}
      className="relative"
      data-testid="cad-viewer"
    >
      <div className="absolute -inset-4 bg-navy/5 rounded-lg rotate-1" aria-hidden="true" />
      <div className="relative bg-white border border-lineblue rounded-md shadow-[0_30px_70px_rgba(11,29,47,0.18)] overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 bg-navy-deep border-b border-navy-light">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-aqua animate-pulse" aria-hidden="true" />
            <span className="font-mono text-[10px] tracking-[0.2em] text-white/70 uppercase">Survey Plan — Interactive Preview</span>
          </div>
          <span className="hidden sm:block font-mono text-[10px] tracking-[0.2em] text-aqua uppercase">GEO-24-118 / 1:500</span>
        </div>

        {!compact && (
          <>
            <div className="flex flex-wrap items-center gap-2 px-4 pt-3 bg-paleslate">
              <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-mutedslate mr-1">Presets</span>
              {Object.entries(PRESETS).map(([key, p]) => (
                <button
                  key={key}
                  data-testid={`layer-preset-${key}`}
                  onClick={() => applyPreset(key)}
                  className="font-mono text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 rounded-sm bg-white border border-lineblue text-steel hover:border-navy hover:text-navy transition-all"
                >
                  {p.label}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap gap-2 px-4 py-3 bg-paleslate border-b border-lineblue">
              {LAYERS.map((l) => (
                <button
                  key={l.key}
                  data-testid={`layer-toggle-${l.key}`}
                  onClick={() => toggle(l.key)}
                  aria-pressed={layers[l.key]}
                  className={`font-mono text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 rounded-sm border transition-all ${
                    layers[l.key]
                      ? "bg-navy text-aqua border-navy"
                      : "bg-white text-mutedslate border-lineblue hover:border-steel"
                  } ${focus === l.key ? "ring-2 ring-aqua ring-offset-1" : ""}`}
                >
                  {layers[l.key] ? "■ " : "□ "}
                  {l.label}
                </button>
              ))}
            </div>
          </>
        )}

        <div className="bg-[#FAFCFE] relative">
          {layers.monuments && (
            <div
              className="absolute left-3 bottom-3 z-10 bg-white/95 border border-lineblue rounded-sm px-3 py-2.5 shadow-sm"
              data-testid="cad-legend"
            >
              <p className="font-mono text-[8px] uppercase tracking-[0.22em] text-mutedslate mb-2">Legend — Monuments</p>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <span className="inline-block h-2.5 w-2.5 rotate-45 bg-navy border border-aqua shrink-0" aria-hidden="true" />
                  <span className="font-mono text-[9px] text-ink">Iron Rod (FND / SET)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-block h-2.5 w-2.5 rounded-full bg-white border-2 border-navy shrink-0" aria-hidden="true" />
                  <span className="font-mono text-[9px] text-ink">Iron Pipe (FND / SET)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-block h-2.5 w-2.5 rounded-full bg-navy-deep border-2 border-aqua shrink-0" aria-hidden="true" />
                  <span className="font-mono text-[9px] text-ink">Boundary Corner</span>
                </div>
              </div>
            </div>
          )}
          <svg viewBox="0 0 640 460" className="w-full h-auto block" role="img" aria-label="Interactive survey plan preview with toggleable layers">
            <defs>
              <pattern id="vgrid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M40 0H0V40" fill="none" stroke="#102A43" strokeOpacity="0.06" />
              </pattern>
            </defs>
            <rect width="640" height="460" fill="url(#vgrid)" />
            {layerWrap("parcels", <Parcels reduced={reduced} />)}
            {layerWrap("contours", <Contours reduced={reduced} />)}
            {layerWrap("road", <RoadRow reduced={reduced} />)}
            {layerWrap("utilities", <Utilities reduced={reduced} />)}
            {layerWrap("improvements", <Improvements reduced={reduced} />)}
            {layerWrap("structures", <Structures reduced={reduced} />)}
            {layerWrap("boundary", <Boundary reduced={reduced} />)}
            {layerWrap("monuments", <Monuments reduced={reduced} />)}
            {layerWrap("spots", <Spots reduced={reduced} />)}
            {layerWrap("text", <PlanText reduced={reduced} />)}
            <g stroke="#94A3B8" strokeWidth="1.2">
              <path d="M20 20 h14 M27 13 v14" />
              <path d="M620 440 h-14 M613 447 v-14" />
            </g>
          </svg>
        </div>

        <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-t border-lineblue bg-white">
          <span className="font-mono text-[10px] tracking-[0.18em] text-mutedslate uppercase hidden sm:inline">N 43°12'18" · W 79°52'04"</span>
          <div className="flex items-center gap-4">
            <a
              href="/sample-survey-plan.svg"
              download="GeoDrafts-Sample-Survey-Plan.svg"
              data-testid="cad-viewer-download"
              className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.15em] uppercase text-steel hover:text-aqua transition-colors"
            >
              <Download className="h-3 w-3" /> Sample Sheet
            </a>
            <a
              href="/sample-print.html"
              target="_blank"
              rel="noopener"
              data-testid="cad-viewer-print"
              className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.15em] uppercase text-steel hover:text-aqua transition-colors"
            >
              Print / PDF
            </a>
            <span className="font-mono text-[10px] tracking-[0.18em] text-aqua uppercase">Sheet 01 / 03 · Rev A</span>
          </div>
        </div>
      </div>

      <motion.div
        className="absolute -top-5 -right-3 sm:-right-6 bg-navy text-white rounded-md px-4 py-3 shadow-xl"
        animate={{ y: [0, -10, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        aria-hidden="true"
      >
        <p className="font-mono text-[9px] tracking-[0.2em] text-aqua uppercase">Drafting Status</p>
        <p className="font-display text-sm font-bold mt-0.5">[LAYER: {STATUS_LABELS[status]}]</p>
      </motion.div>
    </motion.div>
  );
}
